import { useContext, type ReactNode } from 'react'
import { AuthContext, isPlatformUser } from '@retink/web-auth'
import { ShellLayout, type NavEntry, type Crumb } from '@retink/web-ui'

// The staff/admin Console shell — the same left-sidebar grammar as every other
// surface (ShellLayout), skinned bronze by `.surface-console` on <html>
// (main.tsx). What stays console-local is the role-keyed accent: the god-mode
// cue follows the USER's role, not the route — a platform admin drilled into
// an org keeps the platform accent; an org admin never gets it.
interface Props {
  /** Sections; `heading` entries render as sidebar group labels. */
  nav: NavEntry[]
  /** Sign-out handler — wire to signOut from the app's api (env stays app-side). */
  onSignOut: () => void
  /** The scope + entity you're in — rendered as the sidebar scope header. */
  navGroup?: { label: string; entity?: string }
  /** Trail shown in the content top bar; the parent crumb becomes the sidebar back link. */
  breadcrumb?: Crumb[]
  /** Role label for the footer identity block; falls back to the JWT platformRole. */
  roleLabel?: string
  /** Where the footer identity block links for personal account settings. */
  accountPath?: string
  /** Optional action element rendered on the right of the breadcrumb bar. */
  actions?: ReactNode
  /** Passed to <Outlet context> so child pages can consume shell data. */
  outletContext?: unknown
}

export function ConsoleShell(props: Props) {
  const { user } = useContext(AuthContext)
  const platform = isPlatformUser(user)
  const accent = platform ? 'platform' : user?.orgRole === 'admin' ? 'org' : 'tenant'
  return <ShellLayout tag={platform ? 'Platform' : 'Console'} accent={accent} {...props} />
}
